// src/pages/Dashboard.tsx
import { useEffect, useMemo } from 'react'
import { Flame } from 'lucide-react'
import { useAuth } from '../stores/useAuth'
import { useSNR } from '../stores/useSNR'
import { useTasks } from '../stores/useTasks'
import { useStreak } from '../stores/useStreak'
import { SNRGauge } from '../components/SNRGauge'
import { NoiseBudgetBar } from '../components/NoiseBudgetBar'

function QuickStatCard({ label, value, subValue }: { label: string; value: string | number; subValue?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-card-value text-primary">{value}</div>
      {subValue && <div className="body-md text-muted">{subValue}</div>}
      <div className="stat-card-label">{label}</div>
    </div>
  )
}

function StreakBadge({ streak }: { streak: number }) {
  if (streak < 1) return null
  return (
    <div className="badge badge-accent" style={{ gap: 4, padding: '4px 10px' }}>
      <Flame size={12} />
      <span>{streak}d streak</span>
    </div>
  )
}

export default function DashboardPage() {
  const { user } = useAuth()
  const { snrPct, status, signalCount, noiseCount, totalCount, doneCount, noiseTimerSeconds, noiseBudgetSeconds } = useSNR()
  const { load, initRealtime } = useTasks()
  const { streak, load: loadStreak } = useStreak()

  useEffect(() => {
    load()
    loadStreak()
    initRealtime()
  }, [])

  const greeting = useMemo(() => {
    const hrs = new Date().getHours()
    if (hrs < 12) return 'Good morning'
    if (hrs < 18) return 'Good afternoon'
    return 'Good evening'
  }, [])

  const firstName = user?.user_metadata?.full_name?.split(' ')[0]
    ?? user?.email?.split('@')[0]
    ?? 'Founder'

  const focusTimeHrs = Math.floor(noiseTimerSeconds / 3600)
  const focusTimeMins = Math.floor((noiseTimerSeconds % 3600) / 60)
  const { unclassifiedCount } = useSNR()
  const pendingCount = unclassifiedCount

  return (
    <div className="page page-top animate-fade-slide-up">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--xl)' }}>
        <div>
          <p className="body text-muted">{greeting},</p>
          <h1 className="display-md text-primary">{firstName}</h1>
        </div>
        <StreakBadge streak={streak} />
      </div>

      {/* SNR Gauge */}
      <SNRGauge pct={snrPct} status={status} />

      {/* Signal / Noise / Pending counters */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--md)', marginBottom: 'var(--xxl)' }}>
        <div style={{ textAlign: 'center', padding: '0 var(--md)' }}>
          <div className="title text-signal">{signalCount}</div>
          <div className="label text-dim">Signals</div>
        </div>
        <div style={{ width: 1, height: 20, background: 'var(--border)' }} />
        <div style={{ textAlign: 'center', padding: '0 var(--md)' }}>
          <div className="title text-noise">{noiseCount}</div>
          <div className="label text-dim">Noise</div>
        </div>
        <div style={{ width: 1, height: 20, background: 'var(--border)' }} />
        <div style={{ textAlign: 'center', padding: '0 var(--md)' }}>
          <div className="title text-muted">{Math.max(0, pendingCount)}</div>
          <div className="label text-dim">Pending</div>
        </div>
      </div>

      {/* Noise budget */}
      <NoiseBudgetBar spentSeconds={noiseTimerSeconds} budgetSeconds={noiseBudgetSeconds} />

      {/* Quick stats */}
      <p className="label text-muted" style={{ marginBottom: 'var(--sm)' }}>Quick Stats</p>
      <div className="h-scroll" style={{ marginBottom: 'var(--xl)' }}>
        <QuickStatCard label="Done"       value={doneCount}  subValue={`of ${totalCount}`} />
        <QuickStatCard label="Focus Time" value={`${focusTimeHrs}h ${focusTimeMins}m`} />
        <QuickStatCard label="SNR%"       value={`${snrPct}%`} />
        <QuickStatCard label="Signals"    value={signalCount} />
      </div>

      {/* Squad SNR teaser */}
      <div className="card">
        <h2 className="title text-primary" style={{ marginBottom: 'var(--sm)' }}>Squad SNR — Today</h2>
        <p className="body-md text-dim">Upgrade to Pro to join a squad and see the leaderboard.</p>
      </div>
    </div>
  )
}
